import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { DashboardApi, DashboardConfig } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { notifyError, notifySuccess } from '../utils/statusShortcuts';
import {
  INBOX_ATTRIBUTE_STORAGE_PREFIX,
  INBOX_CASEWORK_STORAGE_PREFIX,
  ensureObject,
  formatDateTime,
  formatDetailValue,
  formatInboxSourceLabel,
  getComputedStageMeta,
  getProgressionCustomStatuses,
  getProgressionPercent,
  getProgressionStepStatusLabel,
  getProgressionStepTone,
  getProgressionStepType,
  getTaskStepStateOptions,
  isProgressionStepCompleteStatus,
} from './inquiriesShared';
import {
  StageProgressPillContent,
  getStageProgressPillClassName,
} from './StageProgressPill';
import AppointmentDateTimeRangeField from './AppointmentDateTimeRangeField';
import LocationInput from './LocationInput';
import LocationMapPreview from './LocationMapPreview';
import { getLocationAddress, locationToComparable, normalizeLocationValue } from './locationUtils';
import Timeline from './Timeline';
import './InquiriesPage.css';
import GoogleMeetSection from '../components/OnlineMeetingSection';

const STAGE_OPTIONS = [
  { value: 'new', label: 'Ny' },
  { value: 'in_progress', label: 'Pågår' },
  { value: 'resolved', label: 'Løst' },
];

const CUSTOM_STATUS_VALUE_PREFIX = 'custom:';

const getInquiryStatusMenuOptions = (inquiry) => {
  const customStatuses = getProgressionCustomStatuses(inquiry?.progression);
  if (customStatuses.length === 0) return STAGE_OPTIONS;
  const dynamicLabel = inquiry?.progression?.dynamic_stage_label || getComputedStageMeta(inquiry?.stage, {
    ...inquiry?.progression,
    custom_status: null,
  }).label;
  return [
    { value: '', label: `Følg kundeløp (${dynamicLabel})`, description: 'Dynamisk status' },
    ...customStatuses.map((status) => ({
      value: `${CUSTOM_STATUS_VALUE_PREFIX}${status.key}`,
      label: status.label,
      description: 'Marker som fullført',
    })),
  ];
};

const getInquirySelectedStatusValue = (inquiry) => (
  getProgressionCustomStatuses(inquiry?.progression).length > 0
    ? inquiry?.custom_status_key
      ? `${CUSTOM_STATUS_VALUE_PREFIX}${inquiry.custom_status_key}`
      : ''
    : inquiry?.stage || 'new'
);

const padDatePart = (value) => String(value).padStart(2, '0');

const formatDateTimeInputValue = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return `${date.getFullYear()}-${padDatePart(date.getMonth() + 1)}-${padDatePart(date.getDate())}T${padDatePart(date.getHours())}:${padDatePart(date.getMinutes())}`;
};

const inputDateTimeToIso = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

const normalizeProgressionKey = (value) => (
  typeof value === 'string'
    ? value.trim().toLowerCase().replace(/[^a-z0-9_]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 80)
    : ''
);

const getStepKey = (step) => {
  const explicitKey = normalizeProgressionKey(step?.progression_key || step?.progressionKey);
  if (explicitKey) return explicitKey;
  const itemKey = String(step?.key || step?.id || '').trim();
  const normalizedItemKey = normalizeProgressionKey(itemKey);
  const parentTaskKey = normalizeProgressionKey(step?.parent_task_key || step?.parentTaskKey);
  if (parentTaskKey && normalizedItemKey === parentTaskKey) {
    const parentEventKey = normalizeProgressionKey(step?.parent_event_key || step?.parentEventKey);
    return normalizeProgressionKey(`${parentEventKey ? `${parentEventKey}_` : ''}${parentTaskKey}_field_${normalizedItemKey}`);
  }
  return itemKey;
};

const hasUnifiedProgressionItems = (step) => (
  Array.isArray(step?.tasks)
  || Array.isArray(step?.events)
  || Array.isArray(step?.logFields)
  || Array.isArray(step?.log_fields)
);

const getProgressionItemSortOrder = (item, fallback = 0) => {
  const raw = Number(item?.sortOrder ?? item?.sort_order);
  return Number.isFinite(raw) ? raw : fallback;
};

const sortProgressionFields = (fields = []) => [...fields].sort((a, b) => (
  getProgressionItemSortOrder(a) - getProgressionItemSortOrder(b)
));

const getProgressionStepTasks = (step) => (
  Array.isArray(step?.tasks)
    ? step.tasks.map((task, index) => ({
        ...task,
        type: 'checkbox',
        parent_step_key: step.key || step.id,
        parent_step_label: step.label,
        sortOrder: getProgressionItemSortOrder(task, index),
      }))
    : getProgressionStepType(step) === 'checkbox'
      ? [step]
      : []
);

const getProgressionTaskChildren = (taskGroup) => (
  Array.isArray(taskGroup?.tasks)
    ? taskGroup.tasks.map((childTask, childTaskIndex) => ({
      ...childTask,
      type: 'checkbox',
      parent_step_key: taskGroup.parent_step_key || null,
      parent_step_label: taskGroup.parent_step_label || null,
      parent_event_key: taskGroup.parent_event_key || null,
      parent_event_label: taskGroup.parent_event_label || null,
      parent_task_key: taskGroup.key || taskGroup.id,
      parent_task_label: taskGroup.label,
      required: taskGroup.required === false ? false : childTask.required !== false,
      sortOrder: getProgressionItemSortOrder(childTask, childTaskIndex),
    })).sort((a, b) => (
      getProgressionItemSortOrder(a) - getProgressionItemSortOrder(b)
    ))
    : []
);

const getProgressionStepEvents = (step) => (
  Array.isArray(step?.events)
    ? step.events.map((event, index) => ({
      ...event,
      type: 'appointment',
      parent_step_key: step.key || step.id,
      parent_step_label: step.label,
      sortOrder: getProgressionItemSortOrder(event, index),
    }))
    : !hasUnifiedProgressionItems(step) && getProgressionStepType(step) === 'appointment'
      ? [step]
      : []
);

const getProgressionStepEventTasks = (step) => (
  Array.isArray(step?.events)
    ? step.events.flatMap((event, eventIndex) => (
      Array.isArray(event?.tasks)
        ? event.tasks.map((task, taskIndex) => ({
          ...task,
          type: 'checkbox',
          parent_step_key: step.key || step.id,
          parent_step_label: step.label,
          parent_event_key: event.key || event.id,
          parent_event_label: event.label,
          sortOrder: getProgressionItemSortOrder(event, eventIndex) + ((getProgressionItemSortOrder(task, taskIndex) + 1) / 1000),
        }))
        : []
    ))
    : []
);

const getProgressionAppointmentTasks = (appointment) => (
  Array.isArray(appointment?.tasks)
    ? appointment.tasks.map((task, taskIndex) => ({
      ...task,
      type: 'checkbox',
      parent_step_key: appointment.parent_step_key || null,
      parent_step_label: appointment.parent_step_label || null,
      parent_event_key: appointment.key || appointment.id,
      parent_event_label: appointment.label,
      sortOrder: getProgressionItemSortOrder(task, taskIndex),
    })).sort((a, b) => (
      getProgressionItemSortOrder(a) - getProgressionItemSortOrder(b)
    ))
    : []
);

const getProgressionStepLog = (step) => {
  const fields = Array.isArray(step?.logFields || step?.log_fields || step?.fields)
    ? (step.logFields || step.log_fields || step.fields)
    : [];
  if (fields.length === 0 && getProgressionStepType(step) !== 'log') return null;
  const sortedFields = sortProgressionFields(fields);
  const fieldSortOrders = sortedFields
    .map((field, index) => getProgressionItemSortOrder(field, index))
    .filter((sortOrder) => Number.isFinite(sortOrder));
  return {
    ...step,
    type: 'log',
    fields: sortedFields,
    logFields: sortedFields,
    sortOrder: fieldSortOrders.length > 0
      ? Math.min(...fieldSortOrders)
      : getProgressionItemSortOrder(step),
  };
};

const getProgressionStepItems = (step) => [
  ...getProgressionStepTasks(step).map((item) => ({ ...item, progressionTypeRank: 0 })),
  ...getProgressionStepEvents(step).map((item) => ({ ...item, progressionTypeRank: 1 })),
  ...(getProgressionStepLog(step) ? [{ ...getProgressionStepLog(step), progressionTypeRank: 2 }] : []),
].sort((a, b) => (
  getProgressionItemSortOrder(a) - getProgressionItemSortOrder(b)
  || (a.progressionTypeRank || 0) - (b.progressionTypeRank || 0)
));

const getProgressionStepSavableItems = (step) => [
  ...getProgressionStepItems(step),
  ...getProgressionStepTasks(step).flatMap(getProgressionTaskChildren),
  ...getProgressionStepEventTasks(step),
  ...getProgressionStepEventTasks(step).flatMap(getProgressionTaskChildren),
];

const getProgressionItemUiKey = (item) => {
  const itemKey = getStepKey(item);
  const itemType = getProgressionStepType(item);
  if (itemType === 'log') return `log:${item.parent_step_key || itemKey}`;
  if (itemType === 'appointment') return `event:${itemKey}`;
  if (item.parent_event_key && item.parent_task_key) return `task:${item.parent_event_key}:${item.parent_task_key}:${itemKey}`;
  if (item.parent_event_key) return `task:${item.parent_event_key}:${itemKey}`;
  if (item.parent_task_key) return `task:${item.parent_task_key}:${itemKey}`;
  return `task:${itemKey}`;
};

const getProgressionItemTypeLabel = (item) => {
  const itemType = getProgressionStepType(item);
  if (itemType === 'log') return 'Logg';
  if (itemType === 'appointment') return 'Avtale';
  return item.parent_event_key ? 'Oppgave i avtale' : 'Oppgave';
};

const getProgressionStepComment = (step) => (
  typeof step?.metadata?.comment === 'string' ? step.metadata.comment : ''
);

const getAppointmentAttendanceType = (metadata = {}) => {
  const value = String(metadata?.attendance_type || metadata?.meeting_type || '').trim().toLowerCase();
  return ['digital', 'physical'].includes(value) ? value : '';
};

const getAppointmentLocationAddress = (metadata = {}) => {
  const location = metadata?.location;
  if (!location || typeof location !== 'object' || Array.isArray(location)) return '';
  return getLocationAddress(location);
};

const createProgressionStepDraft = (step, inquiryAssigneeId = null) => {
  const stepType = getProgressionStepType(step);
  if (stepType === 'checkbox') {
    const assignedAccountId = step?.assigned_account_id && step.assigned_account_id !== inquiryAssigneeId
      ? step.assigned_account_id
      : '';
    return {
      assignedAccountId,
      approved: step?.metadata?.approved === true,
      taskStateKey: step?.task_state_key || step?.metadata?.task_state_key || '',
      taskCompletedAt: step?.metadata?.task_completed_at || '',
      taskValue: step?.metadata?.task_value ?? '',
      checked: step?.metadata?.checked === true || step?.metadata?.task_value === true,
      comment: getProgressionStepComment(step),
    };
  }
  if (stepType === 'log') {
    return {
      approved: step?.metadata?.approved === true,
      comment: getProgressionStepComment(step),
    };
  }
  const assignedAccountId = step?.assigned_account_id && step.assigned_account_id !== inquiryAssigneeId
    ? step.assigned_account_id
    : '';
  return {
    assignedAccountId,
    approved: step?.metadata?.approved === true,
    plannedStartAt: formatDateTimeInputValue(step?.planned_start_at),
    plannedEndAt: formatDateTimeInputValue(step?.planned_end_at),
    comment: getProgressionStepComment(step),
    attendanceType: getAppointmentAttendanceType(step?.metadata || {}),
    meetingUrl: typeof step?.metadata?.meeting_url === 'string' ? step.metadata.meeting_url : '',
    locationAddress: getAppointmentLocationAddress(step?.metadata || {}),
    locationValue: normalizeLocationValue(step?.metadata?.location || null),
    useGoogleMeet: step?.metadata?.use_google_meet === true || false,
    useMicrosoftTeams: step?.metadata?.use_microsoft_teams === true || false,
    googleImpersonatedEmail: typeof step?.metadata?.google_impersonated_email === 'string' ? step.metadata.google_impersonated_email : '',
    meetingGuests: Array.isArray(step?.metadata?.google_meet_guests) ? step.metadata.google_meet_guests : [],
    meetingDescription: typeof step?.metadata?.google_meet_description === 'string' ? step.metadata.google_meet_description : '',
    meetingProvider: step?.metadata?.use_google_meet ? 'google'
      : step?.metadata?.use_microsoft_teams ? 'microsoft'
      : null,
    googleMeetUrl: typeof step?.metadata?.google_meet_url === 'string' ? step.metadata.google_meet_url : '',
    microsoftTeamsUrl: typeof step?.metadata?.microsoft_teams_url === 'string' ? step.metadata.microsoft_teams_url : '',
  };
};

const createProgressionLogDraft = (step) => (
  (Array.isArray(step?.fields) ? step.fields : []).reduce((acc, field) => ({
    ...acc,
    [field.key]: field.type === 'boolean' ? false : '',
  }), {})
);

const normalizeTaskDraft = (draft) => ({
  assignedAccountId: String(draft?.assignedAccountId || '').trim(),
  plannedStartAt: String(draft?.plannedStartAt || '').trim(),
  plannedEndAt: String(draft?.plannedEndAt || '').trim(),
  comment: String(draft?.comment || ''),
  attendanceType: String(draft?.attendanceType || '').trim(),
  meetingUrl: String(draft?.meetingUrl || '').trim(),
  locationAddress: String(draft?.locationAddress || '').trim(),
  locationValueKey: locationToComparable(draft?.locationValue || draft?.locationAddress || null),
  useGoogleMeet: Boolean(draft?.meetingProvider === 'google'),
  useMicrosoftTeams: Boolean(draft?.meetingProvider === 'microsoft'),
  meetingProvider: String(draft?.meetingProvider || '').trim(),
  googleImpersonatedEmail: String(draft?.googleImpersonatedEmail || '').trim(),
  meetingGuests: Array.isArray(draft?.meetingGuests) ? draft.meetingGuests.filter(Boolean) : [],
  meetingDescription: String(draft?.meetingDescription || '').trim(),
  googleMeetUrl: String(draft?.googleMeetUrl || '').trim(),
  microsoftTeamsUrl: String(draft?.microsoftTeamsUrl || '').trim(),
  taskCompletedAt: String(draft?.taskCompletedAt || '').trim(),
});

const isStepDraftDirty = (step, draft, inquiryAssigneeId = null) => {
  const stepType = getProgressionStepType(step);
  if (stepType === 'checkbox') {
    const saved = createProgressionStepDraft(step, inquiryAssigneeId);
    return saved.assignedAccountId !== String(draft?.assignedAccountId || '').trim()
      || Boolean(saved.approved) !== Boolean(draft?.approved)
      || saved.taskStateKey !== String(draft?.taskStateKey || '')
      || String(saved.taskValue ?? '') !== String(draft?.taskValue ?? '')
      || Boolean(saved.checked) !== Boolean(draft?.checked)
      || saved.comment !== String(draft?.comment || '')
      || saved.taskCompletedAt !== String(draft?.taskCompletedAt || '');
  }
  if (stepType === 'log') {
    const saved = createProgressionStepDraft(step);
    return Boolean(saved.approved) !== Boolean(draft?.approved)
      || saved.comment !== String(draft?.comment || '');
  }
  const saved = normalizeTaskDraft(createProgressionStepDraft(step, inquiryAssigneeId));
  const current = normalizeTaskDraft(draft);
  return saved.assignedAccountId !== current.assignedAccountId
    || Boolean(saved.approved) !== Boolean(draft?.approved)
    || saved.plannedStartAt !== current.plannedStartAt
    || saved.plannedEndAt !== current.plannedEndAt
    || saved.comment !== current.comment
    || saved.attendanceType !== current.attendanceType
    || saved.meetingUrl !== current.meetingUrl
    || saved.locationAddress !== current.locationAddress
    || saved.locationValueKey !== current.locationValueKey
    || saved.useGoogleMeet !== current.useGoogleMeet
    || saved.useMicrosoftTeams !== current.useMicrosoftTeams
    || saved.googleImpersonatedEmail !== current.googleImpersonatedEmail
    || JSON.stringify(saved.meetingGuests) !== JSON.stringify(current.meetingGuests)
    || saved.meetingDescription !== current.meetingDescription
    || saved.googleMeetUrl !== current.googleMeetUrl
    || saved.microsoftTeamsUrl !== current.microsoftTeamsUrl;
};

const isLogDraftDirty = (draft, fields = []) => (
  (Array.isArray(fields) ? fields : []).some((field) => {
    const value = draft?.[field.key];
    if (field.type === 'boolean') return Boolean(value);
    return String(value ?? '').trim() !== '';
  })
);

const renderDetailValue = (value) => {
  if (value && typeof value === 'object') {
    return <pre className="inquiries-code-block inquiry-detail-inline-code">{formatDetailValue(value)}</pre>;
  }

  return <span>{formatDetailValue(value)}</span>;
};

const looksLikeUrl = (value) => {
  if (typeof value !== 'string') return false;
  const trimmed = value.trim();
  return /^https?:\/\/\S+$/i.test(trimmed) || trimmed.startsWith('data:image/');
};

const extractImageUrls = (payload) => {
  const urls = new Set();

  const visit = (value) => {
    if (!value) return;
    if (Array.isArray(value)) {
      value.forEach((item) => visit(item));
      return;
    }
    if (typeof value === 'object') {
      if (value === null) return;
      if (looksLikeUrl(value.url)) urls.add(value.url.trim());
      if (looksLikeUrl(value.href)) urls.add(value.href.trim());
      if (looksLikeUrl(value.src)) urls.add(value.src.trim());
      if (looksLikeUrl(value.original)) urls.add(value.original.trim());
      if (Array.isArray(value.urls)) value.urls.forEach((url) => visit(url));
      if (Array.isArray(value.files)) value.files.forEach((file) => visit(file));
      if (Array.isArray(value.attachments)) value.attachments.forEach((item) => visit(item));
      return;
    }
    if (looksLikeUrl(value)) {
      urls.add(value.trim());
    }
  };

  visit(payload);
  return Array.from(urls);
};

const normalizeMessageContent = (raw) => {
  if (!raw) {
    return { text: '', images: [] };
  }

  if (typeof raw !== 'string') {
    return { text: String(raw), images: extractImageUrls(raw) };
  }

  const trimmed = raw.trim();
  let parsed;
  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    try {
      parsed = JSON.parse(trimmed);
    } catch {
      parsed = null;
    }
  }

  if (parsed && typeof parsed === 'object') {
    const images = extractImageUrls(parsed);
    if (Array.isArray(parsed.files) && parsed.files.length > 0) {
      const names = parsed.files
        .map((file) => file?.name)
        .filter(Boolean)
        .join(', ');
      return {
        text: names || 'Filopplasting',
        images: images.length ? images : extractImageUrls(parsed.files),
      };
    }
    if (parsed.event === 'file_upload') {
      return {
        text: 'Filopplasting',
        images,
      };
    }
    return {
      text: raw,
      images,
    };
  }

  const images = looksLikeUrl(trimmed) ? [trimmed] : [];
  return { text: raw, images };
};

const normalizeTranscriptDirection = (row) => {
  const rawType = row?.sender_type || row?.senderType || null;
  const normalizedType = typeof rawType === 'string' ? rawType.trim().toLowerCase() : null;
  switch (normalizedType) {
    case 'customer':
    case 'kunde':
      return 'customer';
    case 'bot':
      return 'bot';
    case 'agent':
      return 'agent';
    default:
      return 'bot';
  }
};

const formatMessageTimestamp = (value) => (
  new Date(value).toLocaleString('no-NO', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  })
);

const buildCaseworkDraft = (inquiry) => {
  const fields = Array.isArray(inquiry?.resolved_attribute_definitions) ? inquiry.resolved_attribute_definitions : [];
  const values = inquiry?.attribute_values && typeof inquiry.attribute_values === 'object'
    ? inquiry.attribute_values
    : {};

  return fields.reduce((acc, field) => {
    if (Object.prototype.hasOwnProperty.call(values, field.key)) {
      acc[field.key] = values[field.key];
      return acc;
    }
    if (field.defaultValue !== undefined && field.defaultValue !== null) {
      acc[field.key] = field.defaultValue;
      return acc;
    }
    acc[field.key] = field.type === 'boolean' ? false : '';
    return acc;
  }, {});
};

const getCurrentProductSelectorValue = (inquiry, productSourceField) => {
  if (!productSourceField?.key) return '';
  const attributeValues = inquiry?.attribute_values && typeof inquiry.attribute_values === 'object'
    ? inquiry.attribute_values
    : {};
  if (Object.prototype.hasOwnProperty.call(attributeValues, productSourceField.key)) {
    return String(attributeValues[productSourceField.key] ?? '');
  }
  if (productSourceField.sourceConfig?.value_mode === 'product_name') {
    return String(inquiry?.matched_product?.name || '');
  }
  return inquiry?.bot_product_id ? String(inquiry.bot_product_id) : '';
};

const getInitials = (value) => {
  const normalized = String(value || '').trim();
  if (!normalized) return '?';
  const parts = normalized.split(/\s+/).filter(Boolean);
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return `${parts[0][0] || ''}${parts[parts.length - 1][0] || ''}`.toUpperCase();
};

const resolveProfileImageUrl = (value) => {
  if (!value || typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith('data:') || /^https?:\/\//i.test(trimmed)) return trimmed;
  try {
    return new URL(trimmed, DashboardConfig.apiBaseUrl).href;
  } catch {
    return trimmed;
  }
};

const getProfileImageUrl = (member) => resolveProfileImageUrl(
  member?.profilePictureUrl
  || member?.profile_picture_url
  || member?.avatarUrl
  || member?.avatar_url
  || member?.photoUrl
  || member?.photo_url
  || null,
);

const InquiryDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { selectedCompanyId, selectedCompany, permissions } = useAuth();
  const companyId = selectedCompanyId || selectedCompany?.id || null;
  const inquiryId = Number(id);
  const canView = Boolean(permissions?.canViewInbox || permissions?.canViewInnsikt);
  const canManageInbox = Boolean(permissions?.canManageInbox || permissions?.canEditBots);

  const [detail, setDetail] = useState(null);
  const [assignees, setAssignees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [noteSaving, setNoteSaving] = useState(false);
  const [noteError, setNoteError] = useState(null);
  const [deleteConfirmArmed, setDeleteConfirmArmed] = useState(false);
  const [deleteSaving, setDeleteSaving] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [caseworkDraft, setCaseworkDraft] = useState({});
  const [caseworkSaving, setCaseworkSaving] = useState(false);
  const [caseworkError, setCaseworkError] = useState(null);
  const [progressionDrafts, setProgressionDrafts] = useState({});
  const [progressionLogDrafts, setProgressionLogDrafts] = useState({});
  const [progressionLogDraftRows, setProgressionLogDraftRows] = useState({});
  const [progressionLogEditIds, setProgressionLogEditIds] = useState({});
  const [progressionSavingKey, setProgressionSavingKey] = useState(null);
  const [progressionError, setProgressionError] = useState(null);
  const [selectedProgressionStepKey, setSelectedProgressionStepKey] = useState(null);
  const [expandedProgressionItemKeys, setExpandedProgressionItemKeys] = useState({});
  const [chatMessages, setChatMessages] = useState([]);
  const [chatMessagesLoading, setChatMessagesLoading] = useState(false);
  const [chatMessagesError, setChatMessagesError] = useState(null);
  const [activePopover, setActivePopover] = useState(null);
  const autoMarkedInquiryIdsRef = useRef(new Set());
  const stagePopoverRef = useRef(null);
  const assigneePopoverRef = useRef(null);
  const notePopoverRef = useRef(null);

  const loadInquiry = useCallback(async () => {
    if (!canView || !companyId || !Number.isInteger(inquiryId) || inquiryId <= 0) {
      setDetail(null);
      setAssignees([]);
      return;
    }

    setLoading(true);
    setError(null);
    setActionError(null);
    setNoteError(null);
    setDeleteError(null);
    setDeleteConfirmArmed(false);
    setCaseworkError(null);

    const [detailResult, summaryResult] = await Promise.allSettled([
      DashboardApi.getCompanyInboxDetail(companyId, inquiryId),
      DashboardApi.getCompanyInboxSummary(companyId),
    ]);

    if (detailResult.status === 'fulfilled') {
      setDetail(detailResult.value || null);
    } else {
      setDetail(null);
      const message = detailResult.reason?.message || 'Kunne ikke hente henvendelsen.';
      setError(message);
      notifyError(message, { title: 'Kunne ikke hente henvendelse', source: 'Henvendelser' });
    }

    if (summaryResult.status === 'fulfilled') {
      setAssignees(Array.isArray(summaryResult.value?.assignees) ? summaryResult.value.assignees : []);
    } else {
      setAssignees([]);
    }

    setLoading(false);
  }, [canView, companyId, inquiryId]);

  useEffect(() => {
    loadInquiry().catch((err) => {
      setDetail(null);
      setAssignees([]);
      const message = err.message || 'Kunne ikke hente henvendelsen.';
      setError(message);
      notifyError(message, { title: 'Kunne ikke hente henvendelse', source: 'Henvendelser' });
      setLoading(false);
    });
  }, [loadInquiry]);

  const applyDetailResponse = useCallback((response) => {
    if (!response || typeof response !== 'object') return;
    setDetail(response);
  }, []);

  const handleUpdateInquiry = useCallback(async (payload) => {
    if (!companyId || !inquiryId) return;
    setActionLoading(true);
    setActionError(null);

    try {
      const response = await DashboardApi.updateCompanyInbox(companyId, inquiryId, payload);
      applyDetailResponse(response);
      notifySuccess('Henvendelsen ble oppdatert.', { source: 'Henvendelser' });
      return true;
    } catch (err) {
      const message = err.message || 'Kunne ikke oppdatere henvendelsen.';
      setActionError(message);
      notifyError(message, { title: 'Kunne ikke oppdatere henvendelse', source: 'Henvendelser' });
      return false;
    } finally {
      setActionLoading(false);
    }
  }, [applyDetailResponse, companyId, inquiryId]);

  const handleSubmitNote = async (event) => {
    event.preventDefault();
    if (!companyId || !inquiryId) return;

    const note = noteText.trim();
    if (!note) {
      setNoteError('Skriv et notat før du lagrer.');
      notifyError('Skriv et notat før du lagrer.', { source: 'Henvendelser' });
      return;
    }

    setNoteSaving(true);
    setNoteError(null);

    try {
      const response = await DashboardApi.addCompanyInboxNote(companyId, inquiryId, { note });
      applyDetailResponse(response);
      setNoteText('');
      setActivePopover((current) => (current === 'note' ? null : current));
      notifySuccess('Notatet ble lagret.', { source: 'Henvendelser' });
      return true;
    } catch (err) {
      const message = err.message || 'Kunne ikke lagre notatet.';
      setNoteError(message);
      notifyError(message, { title: 'Kunne ikke lagre notat', source: 'Henvendelser' });
      return false;
    } finally {
      setNoteSaving(false);
    }
  };

  const inquiry = detail?.inquiry || null;
  const progression = inquiry?.progression || null;
  const progressionLogs = useMemo(
    () => (Array.isArray(detail?.progression_logs) ? detail.progression_logs : []),
    [detail?.progression_logs],
  );
  const availableProducts = useMemo(
    () => (Array.isArray(detail?.available_products) ? detail.available_products : []),
    [detail?.available_products],
  );
  const resolvedFields = useMemo(
    () => (Array.isArray(inquiry?.resolved_attribute_definitions) ? inquiry.resolved_attribute_definitions : []),
    [inquiry?.resolved_attribute_definitions],
  );
  const productSourceField = useMemo(
    () => resolvedFields.find((field) => field?.type === 'product') || null,
    [resolvedFields],
  );
  const productOptions = useMemo(() => {
    const options = availableProducts.map((product) => ({ ...product, isHistorical: false }));
    const matchedProduct = inquiry?.matched_product;
    if (!matchedProduct?.id || options.some((product) => Number(product.id) === Number(matchedProduct.id))) {
      return options;
    }
    return [
      {
        ...matchedProduct,
        isHistorical: matchedProduct.status !== 'active',
      },
      ...options,
    ];
  }, [availableProducts, inquiry?.matched_product]);
  const currentProductSelectorValue = useMemo(
    () => getCurrentProductSelectorValue(inquiry, productSourceField),
    [inquiry, productSourceField],
  );

  useEffect(() => {
    setCaseworkDraft(buildCaseworkDraft(inquiry));
    setCaseworkError(null);
  }, [inquiry]);

  useEffect(() => {
    const steps = Array.isArray(progression?.steps) ? progression.steps : [];
    const nextDrafts = {};
    const nextLogDrafts = {};
    steps.forEach((step) => {
      getProgressionStepSavableItems(step).forEach((item) => {
        const stepKey = getStepKey(item);
        if (!stepKey) return;
        nextDrafts[stepKey] = createProgressionStepDraft(item, inquiry?.assigned_to || null);
        if (getProgressionStepType(item) === 'log') {
          nextLogDrafts[stepKey] = createProgressionLogDraft(item);
        }
      });
    });
    setProgressionDrafts(nextDrafts);
    setProgressionLogDrafts(nextLogDrafts);
    setProgressionLogDraftRows({});
    setProgressionLogEditIds({});
    setProgressionError(null);
    setSelectedProgressionStepKey((currentKey) => (
      currentKey && steps.some((step) => getStepKey(step) === currentKey) ? currentKey : null
    ));
  }, [inquiry?.assigned_to, progression]);

  useEffect(() => {
    const chatId = inquiry?.chat_id;
    if (!chatId) {
      setChatMessages([]);
      setChatMessagesError(null);
      setChatMessagesLoading(false);
      return undefined;
    }

    let cancelled = false;
    setChatMessagesLoading(true);
    setChatMessagesError(null);

    DashboardApi.getChatMessages(chatId)
      .then((messages) => {
        if (cancelled) return;
        const normalized = Array.isArray(messages)
          ? messages.map((row) => {
              const { text, images } = normalizeMessageContent(row.message_content || '');
              return {
                message: text,
                images,
                createdAt: row.created_at,
                direction: normalizeTranscriptDirection(row),
              };
            }).filter((message) => message.message || (Array.isArray(message.images) && message.images.length > 0))
          : [];
        setChatMessages(normalized);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err?.status === 404) {
          setChatMessages([]);
          setChatMessagesError(null);
          return;
        }
        setChatMessages([]);
        setChatMessagesError(err.message || 'Kunne ikke hente chat-historikk.');
      })
      .finally(() => {
        if (!cancelled) {
          setChatMessagesLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [inquiry?.chat_id]);

  useEffect(() => {
    if (!activePopover) return undefined;

    const popoverElements = {
      stage: stagePopoverRef.current,
      assignee: assigneePopoverRef.current,
      note: notePopoverRef.current,
    };

    const handlePointerDown = (event) => {
      const activeElement = popoverElements[activePopover];
      if (!activeElement || activeElement.contains(event.target)) {
        return;
      }
      setActivePopover(null);
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setActivePopover(null);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [activePopover]);

  useEffect(() => {
    if (!canView || !companyId || !inquiry?.id || inquiry.is_read || !canManageInbox) {
      return;
    }

    if (autoMarkedInquiryIdsRef.current.has(inquiry.id)) {
      return;
    }

    autoMarkedInquiryIdsRef.current.add(inquiry.id);
    handleUpdateInquiry({ markRead: true }).then((success) => {
      if (!success) {
        autoMarkedInquiryIdsRef.current.delete(inquiry.id);
      }
    });
  }, [canManageInbox, canView, companyId, handleUpdateInquiry, inquiry?.id, inquiry?.is_read]);

  const timelineEntries = useMemo(() => {
    const activityEntries = Array.isArray(detail?.activity) ? detail.activity : [];
    return [...activityEntries].sort((left, right) => {
      const leftTime = new Date(left?.created_at || 0).getTime();
      const rightTime = new Date(right?.created_at || 0).getTime();
      if (Number.isFinite(leftTime) && Number.isFinite(rightTime) && leftTime !== rightTime) {
        return rightTime - leftTime;
      }
      return String(right?.id || '').localeCompare(String(left?.id || ''), 'nb');
    });
  }, [detail?.activity]);
  const progressionLogsByStep = useMemo(() => {
    const map = new Map();
    progressionLogs.forEach((entry) => {
      if (!map.has(entry.step_key)) map.set(entry.step_key, []);
      map.get(entry.step_key).push(entry);
    });
    return map;
  }, [progressionLogs]);
  const progressionSteps = useMemo(
    () => (Array.isArray(progression?.steps) ? progression.steps : []),
    [progression?.steps],
  );
  const mapPreviewLocations = useMemo(() => {
    const locations = [];
    const attributeValues = inquiry?.attribute_values && typeof inquiry.attribute_values === 'object'
      ? inquiry.attribute_values
      : {};
    resolvedFields.forEach((field) => {
      if (field?.type !== 'location') return;
      const location = normalizeLocationValue(attributeValues[field.key]);
      if (location) locations.push(location);
    });
    progressionSteps.forEach((step) => {
      getProgressionStepItems(step).forEach((item) => {
        const location = normalizeLocationValue(item?.metadata?.location);
        if (location) locations.push(location);
      });
    });
    const stepFieldsByKey = new Map(
      progressionSteps
        .map(getProgressionStepLog)
        .filter(Boolean)
        .map((step) => [getStepKey(step), Array.isArray(step?.fields) ? step.fields : []]),
    );
    progressionLogs.forEach((entry) => {
      const fields = stepFieldsByKey.get(entry.step_key) || [];
      fields.forEach((field) => {
        if (field?.type !== 'location') return;
        const location = normalizeLocationValue(entry.values?.[field.key]);
        if (location) locations.push(location);
      });
    });
    const seen = new Set();
    return locations.filter((location) => {
      const key = locationToComparable(location);
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [inquiry?.attribute_values, progressionLogs, progressionSteps, resolvedFields]);
  const selectedProgressionParentStep = useMemo(
    () => progressionSteps.find((step) => getStepKey(step) === selectedProgressionStepKey) || null,
    [progressionSteps, selectedProgressionStepKey],
  );
  const selectedProgressionItems = useMemo(
    () => (selectedProgressionParentStep ? getProgressionStepItems(selectedProgressionParentStep) : []),
    [selectedProgressionParentStep],
  );
  useEffect(() => {
    if (!selectedProgressionParentStep || selectedProgressionItems.length === 0) {
      setExpandedProgressionItemKeys({});
      return;
    }
    const defaultItem = selectedProgressionItems.find((item) => !isProgressionStepCompleteStatus(item))
      || selectedProgressionItems[0];
    const validUiKeys = new Set(selectedProgressionItems.map(getProgressionItemUiKey));
    const defaultUiKey = getProgressionItemUiKey(defaultItem);
    setExpandedProgressionItemKeys((current) => {
      const next = Object.fromEntries(
        Object.entries(current).filter(([key, value]) => value && validUiKeys.has(key)),
      );
      if (!Object.values(next).some(Boolean) && defaultUiKey) {
        next[defaultUiKey] = true;
      }
      return next;
    });
  }, [selectedProgressionItems, selectedProgressionParentStep]);
  const selectedProgressionSaving = selectedProgressionParentStep
    ? getProgressionStepSavableItems(selectedProgressionParentStep).some((item) => (
      progressionSavingKey === getStepKey(item)
      || progressionSavingKey === `${getStepKey(item)}:log`
    ))
    : false;
  const selectedProgressionDirty = selectedProgressionParentStep
    ? getProgressionStepSavableItems(selectedProgressionParentStep).some((item) => {
      const itemKey = getStepKey(item);
      if (getProgressionStepType(item) === 'log') {
        return isStepDraftDirty(item, progressionDrafts[itemKey] || {}, inquiry?.assigned_to || null)
          || Boolean(progressionLogDraftRows[itemKey])
          || Boolean(progressionLogEditIds[itemKey])
          || isLogDraftDirty(progressionLogDrafts[itemKey], item.fields);
      }
      return isStepDraftDirty(item, progressionDrafts[itemKey] || {}, inquiry?.assigned_to || null);
    })
    : false;
  const toggleProgressionItemExpanded = (uiKey) => {
    if (!uiKey) return;
    setExpandedProgressionItemKeys((current) => ({
      ...current,
      [uiKey]: !current[uiKey],
    }));
  };
  const archivedProgressionSteps = useMemo(
    () => (Array.isArray(progression?.archived_steps) ? progression.archived_steps : []),
    [progression?.archived_steps],
  );
  const stageMeta = getComputedStageMeta(inquiry?.stage, progression);
  const statusOptions = getInquiryStatusMenuOptions(inquiry);
  const hasCustomStatusOptions = getProgressionCustomStatuses(progression).length > 0;
  const sourceLabel = formatInboxSourceLabel(inquiry?.source);
  const extraEntries = useMemo(() => Object.entries(ensureObject(inquiry?.extra)), [inquiry?.extra]);
  const visibleExtraEntries = useMemo(
    () => extraEntries.filter(([key]) => (
      !String(key).startsWith(INBOX_CASEWORK_STORAGE_PREFIX)
      && !String(key).startsWith(INBOX_ATTRIBUTE_STORAGE_PREFIX)
    )),
    [extraEntries],
  );
  const assignedMemberLabel = inquiry?.assigned_member?.display_name || inquiry?.assigned_member?.email || 'Ufordelt';
  const assignedProfileImageUrl = getProfileImageUrl(inquiry?.assigned_member);
  const assigneeByAccountId = useMemo(() => (
    new Map((Array.isArray(assignees) ? assignees : []).map((assignee) => [assignee.account_id, assignee]))
  ), [assignees]);
  const shouldRenderChatCard = Boolean(
    inquiry?.chat_id && (chatMessagesLoading || chatMessagesError || chatMessages.length > 0),
  );

  const primaryTitle = inquiry?.display_name || inquiry?.name || `Henvendelse #${inquiryId}`;
  const assignedProductDetailValue = inquiry?.matched_product?.name
    ? `${inquiry.matched_product.name}${inquiry.matched_product.status === 'archived' ? ' - arkivert' : ''}`
    : null;
  const subtitleParts = [
    inquiry?.source ? `Kilde: ${sourceLabel}` : null,
    inquiry?.submitted_at || inquiry?.created_at
      ? `Mottatt ${formatDateTime(inquiry?.submitted_at || inquiry?.created_at)}`
      : null,
    inquiry
      ? (
        inquiry.assigned_member?.display_name || inquiry.assigned_member?.email
          ? `Ansvarlig: ${assignedMemberLabel}`
          : 'Ufordelt'
      )
      : null,
  ].filter(Boolean);

  const detailFields = [
    { label: 'ID', value: inquiry?.id || inquiryId },
    { label: 'Henvendelsestype', value: inquiry?.inquiry_type?.name || '—' },
    { label: 'Kilde', value: sourceLabel },
    { label: 'Produkt', value: assignedProductDetailValue },
    { label: 'Status', value: stageMeta.label },
    {
      label: 'Ansvarlig',
      value: assignedMemberLabel,
    },
    { label: 'Chat-ID', value: inquiry?.chat_id || '—' },
    { label: 'Ekstern ID', value: inquiry?.external_source_id || '—' },
    { label: 'Mottatt', value: formatDateTime(inquiry?.submitted_at || inquiry?.created_at) },
    { label: 'Siste aktivitet', value: formatDateTime(inquiry?.last_activity_at) },
  ];

  const renderProgressionLogItemBody = (item) => {
    const itemKey = getStepKey(item);
    const itemDraft = progressionDrafts[itemKey] || {};
    const logDraft = progressionLogDrafts[itemKey] || {};
    const logDraftOpen = Boolean(progressionLogDraftRows[itemKey]);
    const logEditId = progressionLogEditIds[itemKey] || null;
    const itemLogs = progressionLogsByStep.get(itemKey) || [];
    return (
      <>
        <label className="inquiry-detail-control is-full-width">
          <span>Kommentar</span>
          <textarea
            className="input inquiries-note-textarea inquiry-detail-casework-textarea"
            value={itemDraft.comment || ''}
            disabled={!canManageInbox || selectedProgressionSaving}
            onChange={(event) => updateProgressionDraft(itemKey, { comment: event.target.value })}
            placeholder="Legg til en kommentar for steget"
          />
        </label>

        <div className="inquiry-detail-progression-log-table-wrap">
          <div className="inquiry-detail-log-toolbar">
            {canManageInbox ? (
              <button
                type="button"
                className="button button-secondary button-small"
                disabled={selectedProgressionSaving}
                onClick={() => startProgressionLogDraft(item)}
              >
                Ny føring
              </button>
            ) : null}
          </div>
          <table className="inquiry-detail-progression-log-table">
            <thead>
              <tr>
                {(item.fields || []).map((field) => (
                  <th key={field.key}>{field.label}</th>
                ))}
                <th>Ført av</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {itemLogs.length === 0 && !logDraftOpen ? (
                <tr>
                  <td colSpan={(item.fields || []).length + 2} className="inquiry-detail-log-empty">
                    Ingen føringer.
                  </td>
                </tr>
              ) : null}
              {itemLogs.map((entry) => (
                <tr key={entry.id}>
                  {(item.fields || []).map((field) => (
                    <td key={`${entry.id}-${field.key}`}>{formatDetailValue(entry.values?.[field.key])}</td>
                  ))}
                  <td>
                    <strong>{entry.created_by?.display_name || entry.created_by?.email || 'System'}</strong>
                    <small>{formatDateTime(entry.created_at)}</small>
                  </td>
                  <td className="inquiry-detail-log-actions">
                    {canManageInbox ? (
                      <>
                        <button
                          type="button"
                          className="button button-secondary button-small"
                          disabled={selectedProgressionSaving}
                          onClick={() => startProgressionLogEdit(item, entry)}
                        >
                          Rediger
                        </button>
                        <button
                          type="button"
                          className="button button-secondary button-small"
                          disabled={progressionSavingKey === `${itemKey}:log:${entry.id}`}
                          onClick={() => handleDeleteProgressionLog(itemKey, entry.id)}
                        >
                          Slett
                        </button>
                      </>
                    ) : null}
                  </td>
                </tr>
              ))}
              {logDraftOpen ? (
                <tr className="inquiry-detail-log-draft-row">
                  {(item.fields || []).map((field) => {
                    const value = logDraft?.[field.key];
                    return (
                      <td key={`draft-${field.key}`}>
                        {field.type === 'text' ? (
                          <textarea
                            className="input inquiries-note-textarea inquiry-detail-casework-textarea"
                            value={String(value ?? '')}
                            disabled={!canManageInbox || selectedProgressionSaving}
                            onChange={(event) => updateProgressionLogDraft(itemKey, field.key, event.target.value)}
                            aria-label={field.label}
                          />
                        ) : field.type === 'boolean' ? (
                          <button
                            type="button"
                            className={`inquiry-detail-boolean-toggle ${Boolean(value) ? 'is-yes' : 'is-no'}`}
                            aria-pressed={Boolean(value)}
                            disabled={!canManageInbox || selectedProgressionSaving}
                            onClick={() => updateProgressionLogDraft(itemKey, field.key, !Boolean(value))}
                          >
                            {Boolean(value) ? 'Ja' : 'Nei'}
                          </button>
                        ) : field.type === 'select' ? (
                          <select
                            className="select"
                            value={String(value ?? '')}
                            disabled={!canManageInbox || selectedProgressionSaving}
                            onChange={(event) => updateProgressionLogDraft(itemKey, field.key, event.target.value)}
                            aria-label={field.label}
                          >
                            <option value="">Velg</option>
                            {(field.options || []).map((option) => (
                              <option key={`${itemKey}-${field.key}-${option}`} value={option}>{option}</option>
                            ))}
                          </select>
                        ) : field.type === 'location' ? (
                          <LocationInput
                            value={value}
                            disabled={!canManageInbox || selectedProgressionSaving}
                            onChange={(nextValue) => updateProgressionLogDraft(itemKey, field.key, nextValue)}
                            aria-label={field.label}
                          />
                        ) : (
                          <input
                            className="input"
                            type={field.type === 'date' ? 'date' : field.type === 'datetime' ? 'datetime-local' : field.type === 'integer' ? 'number' : 'text'}
                            value={String(value ?? '')}
                            disabled={!canManageInbox || selectedProgressionSaving}
                            onChange={(event) => updateProgressionLogDraft(itemKey, field.key, event.target.value)}
                            aria-label={field.label}
                          />
                        )}
                      </td>
                    );
                  })}
                  <td>{logEditId ? 'Redigerer føring' : 'Ny føring'}</td>
                  <td />
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </>
    );
  };

  const getProgressionItemAssignee = (item, itemDraft = {}) => {
    const explicitAccountId = String(itemDraft.assignedAccountId || '').trim();
    if (explicitAccountId) {
      return assigneeByAccountId.get(explicitAccountId)
        || item?.assigned_member
        || { account_id: explicitAccountId, display_name: 'Valgt ansvarlig' };
    }
    return inquiry?.assigned_member || null;
  };

  const renderProgressionAssigneePicker = (item, itemKey, itemDraft = {}) => {
    const member = getProgressionItemAssignee(item, itemDraft);
    const label = member?.display_name || member?.email || 'Ufordelt';
    const profileImageUrl = getProfileImageUrl(member);
    return (
      <label className="inquiry-detail-control inquiry-detail-assignee-control">
        <span>Ansvarlig</span>
        <span className="inquiry-detail-assignee-select-shell">
          <span className="inquiry-detail-assignee-avatar" aria-hidden="true">
            {profileImageUrl ? <img src={profileImageUrl} alt="" /> : getInitials(label)}
          </span>
          <select
            className="select inquiry-detail-assignee-select"
            aria-label="Ansvarlig"
            value={itemDraft.assignedAccountId || ''}
            disabled={!canManageInbox || selectedProgressionSaving}
            onChange={(event) => updateProgressionDraft(itemKey, { assignedAccountId: event.target.value })}
          >
            <option value="">{inquiry?.assigned_to ? assignedMemberLabel : 'Ufordelt'}</option>
            {assignees
              .filter((assignee) => assignee.account_id !== inquiry?.assigned_to)
              .map((assignee) => (
                <option key={assignee.account_id} value={assignee.account_id}>
                  {assignee.display_name || assignee.email}
                </option>
              ))}
          </select>
        </span>
      </label>
    );
  };

  const renderProgressionTaskItemBody = (item) => {
    const itemKey = getStepKey(item);
    const itemDraft = progressionDrafts[itemKey] || {};
    const childTasks = getProgressionTaskChildren(item);
    const taskOptions = getTaskStepStateOptions(item);
    const selectedTaskOption = taskOptions.find((option) => option.value === (itemDraft.taskStateKey || ''));
    const isApproved = Boolean(itemDraft.approved);
    const inputType = item.inputType || item.input_type || 'toggle';
    const renderTaskInput = (task, isChild = false) => {
      const taskKey = getStepKey(task);
      const draft = progressionDrafts[taskKey] || {};
      const type = task.inputType || task.input_type || 'toggle';
      const options = getTaskStepStateOptions(task);
      if (type === 'checkmark') {
        return (
          <button
            type="button"
            className={`inquiry-detail-task-check-toggle${draft.checked ? ' is-selected' : ''}`}
            aria-pressed={Boolean(draft.checked)}
            disabled={!canManageInbox || selectedProgressionSaving}
            onClick={() => updateProgressionDraft(taskKey, { checked: !Boolean(draft.checked), taskValue: !Boolean(draft.checked) })}
          >
            {draft.checked ? 'Fullført' : 'Marker fullført'}
          </button>
        );
      }
      if (type === 'comment') {
        return (
          <textarea
            className="input inquiries-note-textarea inquiry-detail-casework-textarea"
            rows={3}
            value={draft.taskValue || ''}
            disabled={!canManageInbox || selectedProgressionSaving}
            onChange={(event) => updateProgressionDraft(taskKey, { taskValue: event.target.value })}
            placeholder="Skriv kommentar"
            aria-label={task.label}
          />
        );
      }
      if (type === 'date') {
        return (
          <input
            className="input"
            type="date"
            value={draft.taskValue || ''}
            disabled={!canManageInbox || selectedProgressionSaving}
            onChange={(event) => updateProgressionDraft(taskKey, { taskValue: event.target.value })}
            aria-label={task.label}
          />
        );
      }
      if (options.length <= 5) {
        return (
          <fieldset className="inquiry-detail-task-status-toggle">
            <legend>Status</legend>
            <div>
              {options.map((option) => {
                const isSelected = option.value === (draft.taskStateKey || '');
                return (
                  <button
                    key={option.value}
                    type="button"
                    className={isSelected ? 'is-selected' : ''}
                    aria-pressed={isSelected}
                    disabled={!canManageInbox || selectedProgressionSaving}
                    onClick={() => {
                        const nextState = isSelected ? '' : option.value;
                        const patch = { taskStateKey: nextState };
                        if (nextState && !draft.taskCompletedAt) {
                          patch.taskCompletedAt = new Date().toISOString().slice(0, 16);
                        }
                        updateProgressionDraft(taskKey, patch);
                      }}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          </fieldset>
        );
      }
      return (
        <label className="inquiry-detail-control">
          <span>{isChild ? 'Status' : 'Status'}</span>
          <select
            className="select"
            value={draft.taskStateKey || ''}
            disabled={!canManageInbox || selectedProgressionSaving}
            onChange={(event) => {
                const nextState = event.target.value;
                const patch = { taskStateKey: nextState };
                if (nextState && !draft.taskCompletedAt) {
                  patch.taskCompletedAt = new Date().toISOString().slice(0, 16);
                }
                updateProgressionDraft(taskKey, patch);
              }}
          >
            <option value="">Ikke valgt</option>
            {options.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </label>
      );
    };
    return (
        <div className="inquiry-detail-task-state-panel">
          <div className="inquiry-detail-task-card-header">
          <span>{childTasks.length > 0 ? 'Felter' : 'Status'}</span>
          <button
            type="button"
            className={`inquiry-detail-step-approval-toggle${isApproved ? ' is-approved' : ''}`}
            aria-pressed={isApproved}
            disabled={!canManageInbox || selectedProgressionSaving}
            onClick={() => updateProgressionDraft(itemKey, { approved: !isApproved })}
          >
            {isApproved ? 'Godkjent' : 'Godkjenn'}
          </button>
        </div>
        {childTasks.length > 0 ? (
          <div className="inquiry-detail-task-child-list">
            {childTasks.map((childTask) => {
              const childTaskKey = getStepKey(childTask);
              const childDraft = progressionDrafts[childTaskKey] || {};
              const selectedChildOption = getTaskStepStateOptions(childTask).find((option) => option.value === (childDraft.taskStateKey || ''));
              const childInputType = childTask.inputType || childTask.input_type || 'toggle';
              const isChildCheckmark = childInputType === 'checkmark';
              const childBaseTone = getProgressionStepTone(childTask);
              const isChildDone = isChildCheckmark ? Boolean(childDraft.checked) : (childBaseTone === 'done' || childBaseTone === 'green');
              const childTone = isChildDone ? 'done' : (isChildCheckmark ? 'todo' : childBaseTone);
              return (
                <div className="inquiry-detail-task-child-row" key={getProgressionItemUiKey(childTask)}>
                  <div className="inquiry-detail-task-child-label-row">
                    {isChildCheckmark ? (
                      <button
                        type="button"
                        className={`inquiry-detail-task-child-check is-${childTone}`}
                        aria-pressed={isChildDone}
                        disabled={!canManageInbox || selectedProgressionSaving}
                        onClick={() => {
                          const nextChecked = !Boolean(childDraft.checked);
                          const patch = { checked: nextChecked, taskValue: nextChecked };
                          if (nextChecked && !childDraft.taskCompletedAt) {
                            patch.taskCompletedAt = new Date().toISOString().slice(0, 16);
                          }
                          updateProgressionDraft(childTaskKey, patch);
                          handleSaveProgressionStep(childTask, patch);
                        }}
                      />
                    ) : (
                      <span className={`inquiry-detail-task-child-check is-${childTone}`} aria-hidden="true" />
                    )}
                    <strong>{childTask.label}</strong>
                  </div>
                  {isChildCheckmark ? null : renderTaskInput(childTask, true)}
                  {isChildCheckmark && childDraft.checked ? (
                    <AppointmentDateTimeRangeField
                      startValue={(childDraft.taskCompletedAt ?? childTask?.metadata?.task_completed_at) || ''}
                      endValue=""
                      disabled={!canManageInbox || selectedProgressionSaving}
                      onChange={({ plannedStartAt }) => updateProgressionDraft(childTaskKey, { taskCompletedAt: plannedStartAt })}
                    />
                  ) : null}
                  {!isChildCheckmark && childDraft.taskStateKey && childDraft.taskStateKey !== 'todo' ? (
                    <AppointmentDateTimeRangeField
                      startValue={(childDraft.taskCompletedAt ?? childTask?.metadata?.task_completed_at) || ''}
                      endValue=""
                      disabled={!canManageInbox || selectedProgressionSaving}
                      onChange={({ plannedStartAt }) => updateProgressionDraft(childTaskKey, { taskCompletedAt: plannedStartAt })}
                    />
                  ) : null}
                  {selectedChildOption?.description ? <p>{selectedChildOption.description}</p> : null}
                </div>
              );
            })}
          </div>
        ) : inputType !== 'toggle' ? null : taskOptions.length <= 5 ? (
          <fieldset className="inquiry-detail-task-status-toggle">
            <legend>Status</legend>
            <div>
              {taskOptions.map((option) => {
                const isSelected = option.value === (itemDraft.taskStateKey || '');
                return (
                  <button
                    key={option.value}
                    type="button"
                    className={isSelected ? 'is-selected' : ''}
                    aria-pressed={isSelected}
                    disabled={!canManageInbox || selectedProgressionSaving}
                    onClick={() => {
                      const nextState = isSelected ? '' : option.value;
                      const patch = { taskStateKey: nextState };
                      if (nextState && !itemDraft.taskCompletedAt) {
                        patch.taskCompletedAt = new Date().toISOString().slice(0, 16);
                      }
                      updateProgressionDraft(itemKey, patch);
                    }}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          </fieldset>
        ) : (
          <label className="inquiry-detail-control">
            <span>Status</span>
            <select
              className="select"
            value={itemDraft.taskStateKey || ''}
              disabled={!canManageInbox || selectedProgressionSaving}
              onChange={(event) => {
                const nextState = event.target.value;
                const patch = { taskStateKey: nextState };
                if (nextState && !itemDraft.taskCompletedAt) {
                  patch.taskCompletedAt = new Date().toISOString().slice(0, 16);
                }
                updateProgressionDraft(itemKey, patch);
              }}
            >
            <option value="">Ikke valgt</option>
            {taskOptions.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </label>
        )}
        {inputType !== 'toggle' && childTasks.length === 0 ? renderTaskInput(item) : null}
        {childTasks.length === 0 && selectedTaskOption?.description ? (
          <p>{selectedTaskOption.description}</p>
        ) : null}
        {itemDraft.taskStateKey && itemDraft.taskStateKey !== 'todo' ? (
          <AppointmentDateTimeRangeField
            startValue={itemDraft.taskCompletedAt || ''}
            endValue=""
            disabled={!canManageInbox || selectedProgressionSaving}
            onChange={({ plannedStartAt }) => updateProgressionDraft(itemKey, { taskCompletedAt: plannedStartAt })}
          />
        ) : null}
        <label className="inquiry-detail-control is-full-width">
          <span>Kommentar</span>
          <textarea
            className="input inquiries-note-textarea inquiry-detail-casework-textarea"
            rows={4}
            value={itemDraft.comment || ''}
            disabled={!canManageInbox || selectedProgressionSaving}
            onChange={(event) => updateProgressionDraft(itemKey, { comment: event.target.value })}
            placeholder="Legg til en kommentar for steget"
          />
        </label>
        {renderProgressionAssigneePicker(item, itemKey, itemDraft)}
      </div>
    );
  };

  const renderProgressionAppointmentItemBody = (item) => {
    const itemKey = getStepKey(item);
    const itemDraft = progressionDrafts[itemKey] || {};
    const appointmentTasks = getProgressionAppointmentTasks(item);
    return (
      <div className="inquiry-detail-step-panel-grid">
        <AppointmentDateTimeRangeField
          startValue={itemDraft.plannedStartAt || ''}
          endValue={itemDraft.plannedEndAt || ''}
          disabled={!canManageInbox || selectedProgressionSaving}
          onChange={(patch) => updateProgressionDraft(itemKey, patch)}
        />
        <fieldset className="inquiry-detail-attendance-control">
          <legend>Oppmøte</legend>
          <div className="inquiry-detail-attendance-toggle">
            <button
              type="button"
              className={itemDraft.attendanceType === 'digital' ? 'is-selected' : ''}
              disabled={!canManageInbox || selectedProgressionSaving}
              onClick={() => updateProgressionDraft(itemKey, { attendanceType: 'digital' })}
            >
              Digitalt
            </button>
            <button
              type="button"
              className={itemDraft.attendanceType === 'physical' ? 'is-selected' : ''}
              disabled={!canManageInbox || selectedProgressionSaving}
              onClick={() => updateProgressionDraft(itemKey, { attendanceType: 'physical' })}
            >
              Fysisk
            </button>
          </div>
        </fieldset>
        {itemDraft.attendanceType === 'digital' ? (
          <>
            <GoogleMeetSection
              draft={itemDraft}
              onUpdateDraft={(patch) => updateProgressionDraft(itemKey, patch)}
              companyId={companyId}
              disabled={!canManageInbox || selectedProgressionSaving}
              saving={progressionSavingKey === itemKey}
              assigneeEmail={inquiry?.assigned_member?.email || ''}
            />
            {!itemDraft.useGoogleMeet ? (
              <label className="inquiry-detail-control is-full-width">
                <span>Lenke</span>
                <input
                  className="input"
                  type="url"
                  value={itemDraft.meetingUrl || ''}
                  disabled={!canManageInbox || selectedProgressionSaving}
                  placeholder="https://"
                  onChange={(event) => updateProgressionDraft(itemKey, { meetingUrl: event.target.value })}
                />
              </label>
            ) : null}
          </>
        ) : null}
        {itemDraft.attendanceType === 'physical' ? (
          <label className="inquiry-detail-control is-full-width">
            <span>Adresse</span>
            <LocationInput
              value={itemDraft.locationValue || itemDraft.locationAddress || ''}
              disabled={!canManageInbox || selectedProgressionSaving}
              onChange={(nextValue) => updateProgressionDraft(itemKey, {
                locationValue: nextValue,
                locationAddress: getLocationAddress(nextValue),
              })}
            />
          </label>
        ) : null}
        <label className="inquiry-detail-control is-full-width">
          <span>Kommentar</span>
          <textarea
            className="input inquiries-note-textarea inquiry-detail-casework-textarea"
            rows={3}
            value={itemDraft.comment || ''}
            disabled={!canManageInbox || selectedProgressionSaving}
            onChange={(event) => updateProgressionDraft(itemKey, { comment: event.target.value })}
          />
        </label>
        {appointmentTasks.length > 0 ? (
          <div className="inquiry-detail-appointment-task-list">
            <div className="inquiry-detail-appointment-task-list-header">
              <span>Oppgaver i avtale</span>
            </div>
            {appointmentTasks.map((task) => (
              <section key={getProgressionItemUiKey(task)} className="inquiry-detail-appointment-task-card">
                <header>
                  <div>
                    <strong>{task.label}</strong>
                  </div>
                  <span className={`inquiry-detail-step-item-status is-${getProgressionStepTone(task)}`}>
                    {getProgressionStepStatusLabel(task)}
                  </span>
                </header>
                {renderProgressionTaskItemBody(task)}
              </section>
            ))}
          </div>
        ) : null}
        {renderProgressionAssigneePicker(item, itemKey, itemDraft)}
      </div>
    );
  };

  const renderProgressionItemBody = (item) => {
    const itemType = getProgressionStepType(item);
    if (itemType === 'log') return renderProgressionLogItemBody(item);
    if (itemType === 'checkbox') return renderProgressionTaskItemBody(item);
    return renderProgressionAppointmentItemBody(item);
  };

  const togglePopover = (popoverKey) => {
    if (!canManageInbox) return;
    setActionError(null);
    setNoteError(null);
    setDeleteError(null);
    setDeleteConfirmArmed(false);
    setActivePopover((current) => (current === popoverKey ? null : popoverKey));
  };

  const handleStageChange = async (nextStatusValue) => {
    const customStatuses = getProgressionCustomStatuses(inquiry?.progression);
    const hasCustomStatuses = customStatuses.length > 0;
    const selectedValue = String(nextStatusValue || '');
    if (selectedValue === getInquirySelectedStatusValue(inquiry)) {
      setActivePopover(null);
      return;
    }

    const payload = hasCustomStatuses
      ? {
        customStatusKey: selectedValue.startsWith(CUSTOM_STATUS_VALUE_PREFIX)
          ? selectedValue.slice(CUSTOM_STATUS_VALUE_PREFIX.length)
          : null,
      }
      : { stage: selectedValue };
    const success = await handleUpdateInquiry(payload);
    if (success) {
      setActivePopover(null);
    }
  };

  const handleDeleteInquiry = async () => {
    if (!companyId || !inquiryId || !canManageInbox || deleteSaving) return;

    if (!deleteConfirmArmed) {
      setDeleteError(null);
      setDeleteConfirmArmed(true);
      return;
    }

    setDeleteSaving(true);
    setDeleteError(null);
    try {
      await DashboardApi.deleteCompanyInbox(companyId, inquiryId);
      notifySuccess('Henvendelsen ble slettet.', { source: 'Henvendelser' });
      navigate('/henvendelser/innboks');
    } catch (err) {
      const message = err.message || 'Kunne ikke slette henvendelsen.';
      setDeleteError(message);
      notifyError(message, { title: 'Kunne ikke slette henvendelse', source: 'Henvendelser' });
    } finally {
      setDeleteSaving(false);
    }
  };

  const handleAssigneeChange = async (nextAssignedAccountId) => {
    const normalizedNextAssignee = nextAssignedAccountId || null;
    if (normalizedNextAssignee === (inquiry?.assigned_to || null)) {
      setActivePopover(null);
      return;
    }

    const success = await handleUpdateInquiry({ assignedAccountId: normalizedNextAssignee });
    if (success) {
      setActivePopover(null);
    }
  };

  const handleProductLinkChange = async (event) => {
    const rawValue = String(event.target.value || '').trim();
    const nextProductId = rawValue ? Number(rawValue) : null;
    const success = await handleUpdateInquiry({ botProductId: nextProductId });
    if (!success) return;
    setCaseworkError(null);
  };

  const handleCaseworkFieldChange = (fieldKey, nextValue) => {
    setCaseworkDraft((prev) => ({
      ...prev,
      [fieldKey]: nextValue,
    }));
    if (caseworkError) {
      setCaseworkError(null);
    }
  };

  const handleCaseworkSave = async () => {
    if (!resolvedFields.length) return;
    setCaseworkSaving(true);
    setCaseworkError(null);
    try {
      const draftProductSelectorValue = productSourceField?.key
        ? String(caseworkDraft[productSourceField.key] ?? '')
        : '';
      const productSelectionChanged = productSourceField
        ? draftProductSelectorValue !== currentProductSelectorValue
        : false;
      const fieldsToPersist = productSelectionChanged
        ? resolvedFields.filter((field) => field.productSpecific !== true)
        : resolvedFields;
      const payload = {};
      fieldsToPersist.forEach((field) => {
        const currentValue = caseworkDraft[field.key];
        payload[field.key] = field.type === 'boolean' ? Boolean(currentValue) : currentValue;
      });
      const success = await handleUpdateInquiry({ attributeValues: payload });
      if (!success) {
        setCaseworkError('Kunne ikke lagre attributtene.');
        notifyError('Kunne ikke lagre attributtene.', { source: 'Henvendelser' });
      }
    } finally {
      setCaseworkSaving(false);
    }
  };

  const updateProgressionDraft = (stepKey, patch) => {
    setProgressionDrafts((prev) => ({
      ...prev,
      [stepKey]: {
        ...(prev[stepKey] || {}),
        ...patch,
      },
    }));
    if (progressionError) setProgressionError(null);
  };

  const updateProgressionLogDraft = (stepKey, fieldKey, value) => {
    setProgressionLogDrafts((prev) => ({
      ...prev,
      [stepKey]: {
        ...(prev[stepKey] || {}),
        [fieldKey]: value,
      },
    }));
    if (progressionError) setProgressionError(null);
  };

  const startProgressionLogDraft = (step) => {
    const stepKey = getStepKey(step);
    if (!stepKey) return;
    setProgressionLogDraftRows((prev) => ({ ...prev, [stepKey]: true }));
    setProgressionLogEditIds((prev) => ({ ...prev, [stepKey]: null }));
    setProgressionLogDrafts((prev) => ({
      ...prev,
      [stepKey]: createProgressionLogDraft(step),
    }));
    if (progressionError) setProgressionError(null);
  };

  const startProgressionLogEdit = (step, entry) => {
    const stepKey = getStepKey(step);
    if (!stepKey || !entry?.id) return;
    setProgressionLogDraftRows((prev) => ({ ...prev, [stepKey]: true }));
    setProgressionLogEditIds((prev) => ({ ...prev, [stepKey]: entry.id }));
    setProgressionLogDrafts((prev) => ({
      ...prev,
      [stepKey]: {
        ...createProgressionLogDraft(step),
        ...(entry.values || {}),
      },
    }));
    if (progressionError) setProgressionError(null);
  };

  const handleSaveProgressionStep = async (step, patch = {}) => {
    const stepKey = getStepKey(step);
    if (!companyId || !inquiryId || !stepKey) return;
    const draft = {
      ...(progressionDrafts[stepKey] || {}),
      ...patch,
    };
    const stepType = getProgressionStepType(step);
    const normalizedDraft = normalizeTaskDraft(draft);

    if (stepType !== 'checkbox' && stepType !== 'log' && draft.meetingProvider === 'google' && !draft.googleImpersonatedEmail) {
      const message = 'Du må verifisere en Google Workspace-e-postadresse før du kan lagre med Google Meet.';
      setProgressionError(message);
      notifyError(message, { title: 'Validering feilet', source: 'Henvendelser' });
      return false;
    }

    if (stepType !== 'checkbox' && stepType !== 'log' && draft.meetingProvider && !draft.plannedStartAt) {
      const message = 'Du må sette en dato for møtet før du kan opprette et videomøte.';
      setProgressionError(message);
      notifyError(message, { title: 'Validering feilet', source: 'Henvendelser' });
      return false;
    }

    if (stepType !== 'checkbox' && stepType !== 'log' && draft.meetingProvider === 'microsoft') {
      try {
        const ms = await DashboardApi.getMicrosoftStatus();
        if (!ms?.connected) {
          const message = 'Du må koble til Microsoft Teams før du kan opprette videomøter.';
          setProgressionError(message);
          notifyError(message, { title: 'Validering feilet', source: 'Henvendelser' });
          return false;
        }
      } catch {
        const message = 'Kunne ikke sjekke Microsoft-tilkoblingen. Prøv igjen.';
        setProgressionError(message);
        return false;
      }
    }

    const payload = stepType === 'checkbox'
      ? {
        assignedAccountId: normalizedDraft.assignedAccountId || null,
        approved: Boolean(draft.approved),
        taskStateKey: draft.taskStateKey || null,
        taskValue: (step.inputType || step.input_type || 'toggle') === 'checkmark'
          ? Boolean(draft.checked)
          : (step.inputType || step.input_type || 'toggle') === 'toggle'
            ? undefined
            : draft.taskValue ?? '',
        comment: normalizedDraft.comment,
        taskCompletedAt: draft.taskCompletedAt || null,
      }
      : stepType === 'log'
        ? {
          approved: Boolean(draft.approved),
          comment: normalizedDraft.comment,
        }
        : {
          assignedAccountId: normalizedDraft.assignedAccountId || null,
          approved: Boolean(draft.approved),
          plannedStartAt: inputDateTimeToIso(normalizedDraft.plannedStartAt),
          plannedEndAt: inputDateTimeToIso(normalizedDraft.plannedEndAt),
          comment: normalizedDraft.comment,
          attendanceType: normalizedDraft.attendanceType || null,
          meetingUrl: normalizedDraft.attendanceType === 'digital' && !draft.meetingProvider ? normalizedDraft.meetingUrl || null : null,
          location: normalizedDraft.attendanceType === 'physical' && (draft.locationValue || normalizedDraft.locationAddress)
            ? (normalizeLocationValue(draft.locationValue) || { address: normalizedDraft.locationAddress })
            : null,
          useGoogleMeet: draft.meetingProvider === 'google',
          useMicrosoftTeams: draft.meetingProvider === 'microsoft',
          googleImpersonatedEmail: draft.meetingProvider === 'google' ? String(draft.googleImpersonatedEmail || '').trim() || null : null,
          googleMeetGuests: draft.meetingProvider ? (Array.isArray(draft.meetingGuests) ? draft.meetingGuests.filter(Boolean) : []) : [],
          googleMeetDescription: draft.meetingProvider ? String(draft.meetingDescription || '').trim() || null : null,
        };
    setProgressionSavingKey(stepKey);
    setProgressionError(null);
    try {
      const response = await DashboardApi.updateCompanyInboxProgression(companyId, inquiryId, stepKey, payload);
      applyDetailResponse(response);
      if (draft.meetingProvider) {
        const savedStep = (response?.inquiry?.progression?.steps || [])
          .find((s) => getStepKey(s) === stepKey);
        if (savedStep?.metadata) {
          updateProgressionDraft(stepKey, {
            googleMeetUrl: savedStep.metadata.google_meet_url || '',
            microsoftTeamsUrl: savedStep.metadata.microsoft_teams_url || '',
          });
        }
      }
      notifySuccess('Kundeløp ble oppdatert.', { source: 'Henvendelser' });
      if (response?._googleSyncError) {
        notifyError(response._googleSyncError, { title: 'Google Meet-synkronisering feilet', source: 'Henvendelser' });
      }
      return true;
    } catch (err) {
      const message = err.message || 'Kunne ikke oppdatere kundeløp.';
      setProgressionError(message);
      notifyError(message, { title: 'Kunne ikke oppdatere kundeløp', source: 'Henvendelser' });
      return false;
    } finally {
      setProgressionSavingKey(null);
    }
  };

  const handleSaveProgressionLog = async (step) => {
    const stepKey = getStepKey(step);
    if (!companyId || !inquiryId || !stepKey) return;
    const editId = progressionLogEditIds[stepKey] || null;
    setProgressionSavingKey(`${stepKey}:log`);
    setProgressionError(null);
    try {
      const payload = { values: progressionLogDrafts[stepKey] || {} };
      const response = editId
        ? await DashboardApi.updateCompanyInboxProgressionLog(companyId, inquiryId, stepKey, editId, payload)
        : await DashboardApi.createCompanyInboxProgressionLog(companyId, inquiryId, stepKey, payload);
      applyDetailResponse(response);
      setProgressionLogDraftRows((prev) => ({ ...prev, [stepKey]: false }));
      setProgressionLogEditIds((prev) => ({ ...prev, [stepKey]: null }));
      notifySuccess(editId ? 'Loggen ble oppdatert.' : 'Loggen ble lagt til.', { source: 'Henvendelser' });
      return true;
    } catch (err) {
      const message = err.message || 'Kunne ikke lagre logg.';
      setProgressionError(message);
      notifyError(message, { title: 'Kunne ikke lagre logg', source: 'Henvendelser' });
      return false;
    } finally {
      setProgressionSavingKey(null);
    }
  };

  const handleDiscardProgressionChanges = (step) => {
    const items = getProgressionStepSavableItems(step);
    items.forEach((item) => {
      const stepKey = getStepKey(item);
      if (!stepKey) return;
      if (getProgressionStepType(item) === 'log') {
        setProgressionDrafts((prev) => ({
          ...prev,
          [stepKey]: createProgressionStepDraft(item, inquiry?.assigned_to || null),
        }));
        setProgressionLogDrafts((prev) => ({
          ...prev,
          [stepKey]: createProgressionLogDraft(item),
        }));
        setProgressionLogDraftRows((prev) => ({ ...prev, [stepKey]: false }));
        setProgressionLogEditIds((prev) => ({ ...prev, [stepKey]: null }));
      } else {
        setProgressionDrafts((prev) => ({
          ...prev,
          [stepKey]: createProgressionStepDraft(item, inquiry?.assigned_to || null),
        }));
      }
    });
    setProgressionError(null);
  };

  const handleSaveProgressionPanel = async (step) => {
    if (!step) return;
    const items = getProgressionStepSavableItems(step);
    const draftSnapshots = { ...progressionDrafts };
    const logDraftSnapshots = { ...progressionLogDrafts };
    const logDraftRowSnapshots = { ...progressionLogDraftRows };
    const logEditIdSnapshots = { ...progressionLogEditIds };
    for (const item of items) {
      const stepKey = getStepKey(item);
      if (!stepKey) continue;
      if (getProgressionStepType(item) === 'log') {
        const approvalDirty = isStepDraftDirty(item, draftSnapshots[stepKey] || {}, inquiry?.assigned_to || null);
        const logDirty = logDraftRowSnapshots[stepKey]
          || Boolean(logEditIdSnapshots[stepKey])
          || isLogDraftDirty(logDraftSnapshots[stepKey], item.fields);
        if (logDirty) {
          const logSaved = await handleSaveProgressionLog(item);
          if (!logSaved) return;
        }
        if (approvalDirty) {
          const stepSaved = await handleSaveProgressionStep(item, draftSnapshots[stepKey] || {});
          if (!stepSaved) return;
        }
      } else if (isStepDraftDirty(item, draftSnapshots[stepKey] || {}, inquiry?.assigned_to || null)) {
        const isCheckmarkChild = item.parent_task_key && (item.inputType || item.input_type) === 'checkmark';
        if (isCheckmarkChild) continue;
        const stepSaved = await handleSaveProgressionStep(item, draftSnapshots[stepKey] || {});
        if (!stepSaved) return;
      }
    }
  };

  const handleDeleteProgressionLog = async (stepKey, logId) => {
    if (!companyId || !inquiryId || !stepKey || !logId) return;
    setProgressionSavingKey(`${stepKey}:log:${logId}`);
    setProgressionError(null);
    try {
      const response = await DashboardApi.deleteCompanyInboxProgressionLog(companyId, inquiryId, stepKey, logId);
      applyDetailResponse(response);
      notifySuccess('Loggen ble slettet.', { source: 'Henvendelser' });
    } catch (err) {
      const message = err.message || 'Kunne ikke slette logg.';
      setProgressionError(message);
      notifyError(message, { title: 'Kunne ikke slette logg', source: 'Henvendelser' });
    } finally {
      setProgressionSavingKey(null);
    }
  };

  if (!canView) {
    return <Navigate to="/" replace />;
  }

  if (!companyId) {
    return (
      <div className="page">
        <div className="card">
          <h1>Henvendelser</h1>
          <p>Velg et selskap for å åpne en henvendelse.</p>
        </div>
      </div>
    );
  }

  if (!Number.isInteger(inquiryId) || inquiryId <= 0) {
    return (
      <div className="page">
        <div className="card">
          <h1>Henvendelse</h1>
          <p>Ugyldig henvendelses-ID.</p>
          <Link to="/henvendelser/innboks" className="button button-secondary">
            Tilbake til innboks
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="page inquiry-detail-page">
      <div className="page-header">
        <div>
          <p className="page-eyebrow">Henvendelse #{inquiryId}</p>
          <h1>{primaryTitle}</h1>
          <p className="page-subtitle">{subtitleParts.join(' · ')}</p>
        </div>
        <div className="page-actions">
          <Link to="/henvendelser/innboks" className="button button-secondary">
            Tilbake til innboks
          </Link>
          {inquiry?.chat_id ? (
            <Link
              to={`/chat-historikk?chatId=${encodeURIComponent(inquiry.chat_id)}`}
              className="button button-secondary"
            >
              Se chat-historikk
            </Link>
          ) : null}
        </div>
      </div>

      {loading ? (
        <article className="card inquiries-empty-state">
          <div className="loading-spinner" />
          <p>Laster henvendelse…</p>
        </article>
      ) : !inquiry ? (
        <article className="card inquiries-empty-state">
          <p>Henvendelsen ble ikke funnet.</p>
          <Link to="/henvendelser/innboks" className="button button-secondary">
            Tilbake til innboks
          </Link>
        </article>
      ) : (
        <div className="inquiry-detail-layout">
          <div className="inquiry-detail-main">
            {progression?.configured ? (
              <article className={`card inquiry-detail-progression-card inquiry-detail-progression-timeline-card${selectedProgressionParentStep ? ' is-step-open' : ''}`}>
                <header className="card-header">
                  <div>
                    <h2>Kundeløp</h2>
                    <p className="card-subtitle">
                      {getProgressionPercent(progression) ?? 0}% progresjon
                    </p>
                  </div>
                </header>

                {progressionError ? <p className="inquiries-note-error">{progressionError}</p> : null}

                <div className="inquiry-detail-progress-timeline" aria-label="Kundeløp">
                  {progressionSteps.map((step, index) => {
                    const stepKey = getStepKey(step);
                    const tone = getProgressionStepTone(step);
                    const isSelected = selectedProgressionStepKey === stepKey;
                    return (
                      <button
                        key={stepKey}
                        type="button"
                        className={[
                          'inquiry-detail-progress-step-button',
                          `is-${tone}`,
                          isSelected ? 'is-selected' : '',
                        ].filter(Boolean).join(' ')}
                        onClick={() => setSelectedProgressionStepKey((current) => (current === stepKey ? null : stepKey))}
                        aria-pressed={isSelected}
                      >
                        <span className="inquiry-detail-progress-step-index">{index + 1}</span>
                        <span className="inquiry-detail-progress-step-label">{step.label}</span>
                        <small className="inquiry-detail-progress-step-status">{getProgressionStepStatusLabel(step)}</small>
                      </button>
                    );
                  })}
                </div>

                {selectedProgressionParentStep ? (
                  <div className="inquiry-detail-step-panel">
                    <header className="inquiry-detail-step-panel-header">
                      <h3>{selectedProgressionParentStep.label}</h3>
                    </header>

                    {selectedProgressionItems.length === 0 ? (
                      <div className="inquiry-detail-step-empty">Ingen innholdselementer.</div>
                    ) : (
                      <div className="inquiry-detail-step-item-list">
                        {selectedProgressionItems.map((item) => {
                          const uiKey = getProgressionItemUiKey(item);
                          const itemKey = getStepKey(item);
                          const itemDraft = progressionDrafts[itemKey] || {};
                          const isExpanded = Boolean(expandedProgressionItemKeys[uiKey]);
                          const tone = getProgressionStepTone(item);
                          const isApproved = Boolean(itemDraft.approved);
                          const itemType = getProgressionStepType(item);
                          return (
                            <section key={uiKey} className={`inquiry-detail-step-item-row is-${tone}${isExpanded ? ' is-expanded' : ''}`}>
                              <div className="inquiry-detail-step-item-header">
                                <button
                                  type="button"
                                  className="inquiry-detail-step-item-main-button"
                                  onClick={() => toggleProgressionItemExpanded(uiKey)}
                                  aria-expanded={isExpanded}
                                >
                                  <span className="inquiry-detail-step-item-main">
                                    <span className="inquiry-detail-step-item-type">{getProgressionItemTypeLabel(item)}</span>
                                    <strong>{item.label}</strong>
                                    {item.parent_event_label || item.parent_task_label ? <small>{item.parent_event_label || item.parent_task_label}</small> : null}
                                  </span>
                                </button>
                                {itemType !== 'checkbox' ? (
                                  <button
                                    type="button"
                                    className={`inquiry-detail-step-approval-toggle${isApproved ? ' is-approved' : ''}`}
                                    aria-pressed={isApproved}
                                    disabled={!canManageInbox || selectedProgressionSaving}
                                    onClick={() => updateProgressionDraft(itemKey, { approved: !isApproved })}
                                  >
                                    {isApproved ? 'Godkjent' : 'Godkjenn'}
                                  </button>
                                ) : null}
                                <span className={`inquiry-detail-step-item-status is-${tone}`}>{getProgressionStepStatusLabel(item)}</span>
                                <button
                                  type="button"
                                  className="inquiry-detail-step-item-action"
                                  onClick={() => toggleProgressionItemExpanded(uiKey)}
                                  aria-expanded={isExpanded}
                                >
                                  {isExpanded ? 'Lukk' : 'Åpne'}
                                </button>
                              </div>
                              {isExpanded ? (
                                <div className="inquiry-detail-step-item-body">
                                  {renderProgressionItemBody(item)}
                                </div>
                              ) : null}
                            </section>
                          );
                        })}
                      </div>
                    )}


                  </div>
                ) : null}
              </article>
            ) : null}

            {canManageInbox && selectedProgressionDirty ? (
              <div className="inquiry-detail-step-save-prompt" role="status" aria-live="polite">
                <div>
                  <strong>Ulagrede endringer</strong>
                  <p>Endringene forkastes hvis du forlater siden.</p>
                </div>
                <div className="inquiry-detail-step-save-prompt-actions">
                  <button
                    type="button"
                    className="button button-secondary button-small"
                    disabled={selectedProgressionSaving}
                    onClick={() => handleDiscardProgressionChanges(selectedProgressionParentStep)}
                  >
                    Forkast
                  </button>
                  <button
                    type="button"
                    className="button button-primary button-small"
                    disabled={selectedProgressionSaving}
                    onClick={() => handleSaveProgressionPanel(selectedProgressionParentStep)}
                  >
                    {selectedProgressionSaving ? 'Lagrer…' : 'Lagre endringer'}
                  </button>
                </div>
              </div>
            ) : null}

            <article className={`card inquiry-detail-overview-card${activePopover === 'stage' || activePopover === 'assignee' ? ' inquiry-detail-popover-card-active' : ''}`}>
              <header className="card-header">
                <div>
                  <h2>Oversikt</h2>
                  <p className="card-subtitle">Delbar saksvisning for hele henvendelsen.</p>
                </div>
              </header>

              <div className="inquiry-detail-overview-strip">
                <div className="inquiry-detail-overview-item">
                  <span className="inquiry-detail-overview-label">Status</span>
                  <div className="inquiry-detail-popover-anchor" ref={stagePopoverRef}>
                    <button
                      type="button"
                      className={getStageProgressPillClassName('inquiry-status inquiry-pill-button', stageMeta.className, progression)}
                      disabled={!canManageInbox || actionLoading || (progression?.configured && !hasCustomStatusOptions)}
                      onClick={() => togglePopover('stage')}
                      aria-haspopup="dialog"
                      aria-expanded={activePopover === 'stage'}
                    >
                      <StageProgressPillContent label={stageMeta.label} progression={progression} />
                    </button>
                    {activePopover === 'stage' ? (
                      <div className="inquiry-detail-popover" role="dialog" aria-label="Velg stage">
                        <div className="inquiry-detail-popover-list">
                          {statusOptions.map((option) => (
                            <button
                              key={option.value}
                              type="button"
                              className={`inquiry-detail-option ${option.value === getInquirySelectedStatusValue(inquiry) ? 'is-active' : ''}`}
                              disabled={actionLoading}
                              onClick={() => handleStageChange(option.value)}
                            >
                              <span>
                                {option.label}
                                {option.description ? <small>{option.description}</small> : null}
                              </span>
                              {option.value === getInquirySelectedStatusValue(inquiry) ? <span>Valgt</span> : null}
                            </button>
                          ))}
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>

                <div className="inquiry-detail-overview-item inquiry-detail-overview-assignee">
                  <span className="inquiry-detail-overview-label">Følges opp av</span>
                  <div className="inquiry-detail-popover-anchor" ref={assigneePopoverRef}>
                    <button
                      type="button"
                      className="inquiry-detail-assignee-card inquiry-pill-button"
                      disabled={!canManageInbox || actionLoading}
                      onClick={() => togglePopover('assignee')}
                      aria-haspopup="dialog"
                      aria-expanded={activePopover === 'assignee'}
                    >
                      <span className="inquiry-detail-assignee-avatar" aria-hidden="true">
                        {assignedProfileImageUrl ? (
                          <img src={assignedProfileImageUrl} alt="" />
                        ) : (
                          getInitials(assignedMemberLabel)
                        )}
                      </span>
                      <span>{assignedMemberLabel}</span>
                    </button>
                    {activePopover === 'assignee' ? (
                      <div className="inquiry-detail-popover inquiry-detail-popover-wide" role="dialog" aria-label="Velg ansvarlig">
                        <div className="inquiry-detail-popover-list">
                          <button
                            type="button"
                            className={`inquiry-detail-option ${!inquiry.assigned_to ? 'is-active' : ''}`}
                            disabled={actionLoading}
                            onClick={() => handleAssigneeChange(null)}
                          >
                            <span>Ufordelt</span>
                            {!inquiry.assigned_to ? <span>Valgt</span> : null}
                          </button>
                          {assignees.map((assignee) => {
                            const label = assignee.display_name || assignee.email;
                            const isActive = assignee.account_id === inquiry.assigned_to;
                            return (
                              <button
                                key={assignee.account_id}
                                type="button"
                                className={`inquiry-detail-option ${isActive ? 'is-active' : ''}`}
                                disabled={actionLoading}
                                onClick={() => handleAssigneeChange(assignee.account_id)}
                              >
                                <span>{label}</span>
                                {isActive ? <span>Valgt</span> : null}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>

              {actionError ? <p className="inquiries-note-error">{actionError}</p> : null}

              <div className="inquiry-detail-copy">
                {inquiry.subject ? (
                  <div className="inquiry-detail-copy-block">
                    <h3>Emne</h3>
                    <p>{inquiry.subject}</p>
                  </div>
                ) : null}

                {inquiry.summary ? (
                  <div className="inquiry-detail-copy-block">
                    <h3>Oppsummering</h3>
                    <p>{inquiry.summary}</p>
                  </div>
                ) : null}

                {!inquiry.subject && !inquiry.summary ? (
                  <p className="card-subtitle">
                    Ingen emne- eller oppsummeringstekst er registrert for denne henvendelsen.
                  </p>
                ) : null}
              </div>

            </article>

            {archivedProgressionSteps.length > 0 ? (
              <article className="card inquiry-detail-archived-progression-card">
                <header className="card-header">
                  <div>
                    <h2>Tidligere kundeløp</h2>
                    <p className="card-subtitle">Historikk fra steg som ikke lenger finnes i kundeløpet.</p>
                  </div>
                </header>
                <ol className="inquiry-detail-progression-list">
                  {archivedProgressionSteps.map((step) => {
                    const stepKey = getStepKey(step);
                    const stepLogs = progressionLogsByStep.get(stepKey) || [];
                    const tone = getProgressionStepTone(step);
                    return (
                      <li key={stepKey} className={`inquiry-detail-progression-step is-${tone} is-archived`}>
                        <div className="inquiry-detail-progression-step-main">
                          <span className="inquiry-detail-progression-marker" aria-hidden="true" />
                          <div>
                            <h3>{step.label}</h3>
                            <p>
                              {step.scheduled_date ? `Dato ${step.scheduled_date}` : 'Ingen dato'}
                              {step.log_count ? ` · ${step.log_count} logg` : ''}
                            </p>
                          </div>
                        </div>
                        {stepLogs.length > 0 ? (
                          <div className="inquiry-detail-progression-log-list">
                            {stepLogs.map((entry) => (
                              <div key={entry.id} className="inquiry-detail-progression-log-entry">
                                <div>
                                  <strong>{formatDateTime(entry.created_at)}</strong>
                                  <small>{entry.created_by?.display_name || entry.created_by?.email || 'System'}</small>
                                </div>
                                <dl>
                                  {Object.entries(entry.values || {}).map(([key, value]) => (
                                    <div key={key}>
                                      <dt>{key}</dt>
                                      <dd>{formatDetailValue(value)}</dd>
                                    </div>
                                  ))}
                                </dl>
                              </div>
                            ))}
                          </div>
                        ) : null}
                      </li>
                    );
                  })}
                </ol>
              </article>
            ) : null}

            <article className="card">
              <header className="card-header">
                <div>
                  <h2>Attributter</h2>
                  <p className="card-subtitle">
                    Basisattributter fra henvendelsestypen og eventuelle produktspesifikke attributter for valgt produkt.
                  </p>
                </div>
              </header>

              <div className="inquiry-detail-casework">
                {!productSourceField ? (
                  <label className="inquiry-detail-control">
                    <span>Koblet produkt</span>
                    <select
                      className="select"
                      value={String(inquiry?.bot_product_id || '')}
                      onChange={handleProductLinkChange}
                      disabled={!canManageInbox || actionLoading}
                    >
                      <option value="">Ingen produktkobling</option>
                      {productOptions.map((product) => (
                        <option key={product.id} value={String(product.id)}>
                          {product.name}{product.isHistorical ? ' (arkivert)' : ''}
                        </option>
                      ))}
                    </select>
                  </label>
                ) : null}

                {inquiry?.matched_product ? (
                  <p className="input-hint">
                    Produktkobling: {inquiry.matched_product.name}
                    {inquiry.matched_product.status === 'archived' ? ' (arkivert)' : ''}
                  </p>
                ) : null}

                {caseworkError ? <p className="inquiries-note-error">{caseworkError}</p> : null}

                {resolvedFields.length === 0 && productSourceField && !inquiry?.bot_product_id ? (
                  <p className="card-subtitle">
                    Velg et produkt i produktattributtet for å laste produktspesifikke attributter for denne henvendelsen.
                  </p>
                ) : resolvedFields.length === 0 ? (
                  <p className="card-subtitle">
                    Ingen attributter er konfigurert for denne henvendelsen ennå.
                  </p>
                ) : (
                  <>
                    <div className="inquiry-detail-casework-grid">
                      {resolvedFields.map((field) => {
                        const rawFieldValue = caseworkDraft[field.key];
                        const fieldValue = field.type === 'location' && rawFieldValue && typeof rawFieldValue === 'object'
                          ? (rawFieldValue.address || rawFieldValue.formatted_address || rawFieldValue.formattedAddress || '')
                          : rawFieldValue;
                        const isProductSource = field?.sourceConfig?.source === 'products';
                        return (
                        <label
                          key={field.key}
                          className={`inquiry-detail-control ${field.type === 'text' || field.type === 'location' ? 'is-full-width' : ''}`}
                        >
                          <span>{field.label}</span>
                          {field.type === 'text' ? (
                            <textarea
                              className="input inquiries-note-textarea inquiry-detail-casework-textarea"
                              value={String(fieldValue ?? '')}
                              disabled={!canManageInbox || caseworkSaving || actionLoading}
                              placeholder={field.placeholder || ''}
                              onChange={(event) => handleCaseworkFieldChange(field.key, event.target.value)}
                            />
                          ) : field.type === 'boolean' ? (
                            <button
                              type="button"
                              className={`inquiry-detail-boolean-toggle ${Boolean(fieldValue) ? 'is-yes' : 'is-no'}`}
                              aria-pressed={Boolean(fieldValue)}
                              disabled={!canManageInbox || caseworkSaving || actionLoading}
                              onClick={() => handleCaseworkFieldChange(field.key, !Boolean(fieldValue))}
                            >
                              {Boolean(fieldValue) ? 'Ja' : 'Nei'}
                            </button>
                          ) : isProductSource ? (
                            <select
                              className="select"
                              value={String(fieldValue ?? '')}
                              disabled={!canManageInbox || caseworkSaving || actionLoading}
                              onChange={(event) => handleCaseworkFieldChange(field.key, event.target.value)}
                            >
                              <option value="">Velg produkt</option>
                              {productOptions.map((product) => {
                                const optionValue = field.sourceConfig?.value_mode === 'product_name'
                                  ? product.name
                                  : String(product.id);
                                return (
                                  <option key={`${field.key}-${product.id}`} value={optionValue}>
                                    {product.name}{product.isHistorical ? ' (arkivert)' : ''}
                                  </option>
                                );
                              })}
                            </select>
                          ) : field.type === 'select' ? (
                            <select
                              className="select"
                              value={String(fieldValue ?? '')}
                              disabled={!canManageInbox || caseworkSaving || actionLoading}
                              onChange={(event) => handleCaseworkFieldChange(field.key, event.target.value)}
                            >
                              <option value="">Velg</option>
                              {(Array.isArray(field.options) ? field.options : []).map((option) => (
                                <option key={`${field.key}-${option}`} value={option}>
                                  {option}
                                </option>
                              ))}
                            </select>
                          ) : (
                            field.type === 'location' ? (
                              <LocationInput
                                value={rawFieldValue}
                                disabled={!canManageInbox || caseworkSaving || actionLoading}
                                placeholder={field.placeholder || undefined}
                                onChange={(nextValue) => handleCaseworkFieldChange(field.key, nextValue)}
                                aria-label={field.label}
                              />
                            ) : (
                              <input
                                className="input"
                                type={field.type === 'date' ? 'date' : field.type === 'datetime' ? 'datetime-local' : field.type === 'integer' ? 'number' : 'text'}
                                value={field.type === 'boolean' ? undefined : String(fieldValue ?? '')}
                                disabled={!canManageInbox || caseworkSaving || actionLoading}
                                placeholder={field.placeholder || ''}
                                onChange={(event) => handleCaseworkFieldChange(field.key, event.target.value)}
                              />
                            )
                          )}
                        </label>
                        );
                      })}
                    </div>
                    <div className="inquiry-detail-casework-actions">
                      <button
                        type="button"
                        className="button button-primary"
                        onClick={handleCaseworkSave}
                        disabled={!canManageInbox || caseworkSaving || actionLoading}
                      >
                        {caseworkSaving ? 'Lagrer…' : 'Lagre attributter'}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </article>

            {shouldRenderChatCard ? (
            <article className="card inquiry-detail-chat-card">
              <header className="card-header inquiry-detail-chat-header">
                <div>
                  <h2>Chat-historikk</h2>
                  <p className="card-subtitle">
                    Kompakt oversikt over samtalen som ledet til denne henvendelsen.
                  </p>
                </div>
                <Link
                  to={`/chat-historikk?chatId=${encodeURIComponent(inquiry.chat_id)}`}
                  className="button button-secondary"
                >
                  Åpne full historikk
                </Link>
              </header>

              {chatMessagesLoading ? (
                <div className="inquiry-detail-chat-empty">
                  <div className="loading-spinner" />
                  <p>Laster chat-historikk…</p>
                </div>
              ) : chatMessagesError ? (
                <p className="inquiries-note-error">{chatMessagesError}</p>
              ) : chatMessages.length === 0 ? (
                <p className="card-subtitle">Ingen meldinger er registrert på denne chatten ennå.</p>
              ) : (
                <div className="inquiry-detail-chat-transcript">
                  {chatMessages.map((message, index) => (
                    <div
                      key={`${inquiry.chat_id}-${index}`}
                      className={`inquiry-detail-chat-row is-${message.direction}`}
                    >
                      <div className="inquiry-detail-chat-meta">
                        <span>
                          {message.direction === 'customer'
                            ? 'Kunde'
                            : message.direction === 'agent'
                              ? 'Agent'
                              : 'Bot'}
                        </span>
                        <span>{formatMessageTimestamp(message.createdAt)}</span>
                      </div>
                      <div className="inquiry-detail-chat-bubble">
                        {message.message ? (
                          <p className="inquiry-detail-chat-text">{message.message}</p>
                        ) : null}
                        {Array.isArray(message.images) && message.images.length > 0 ? (
                          <div className="inquiry-detail-chat-images">
                            {message.images.map((url) => (
                              <a key={url} href={url} target="_blank" rel="noopener noreferrer">
                                <img src={url} alt="Opplastet vedlegg" />
                              </a>
                            ))}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </article>
            ) : null}

            {visibleExtraEntries.length > 0 ? (
              <article className="card">
                <header className="card-header">
                  <div>
                    <h2>Ekstrafelter</h2>
                    <p className="card-subtitle">Dynamiske felter fra `extra` vises her per henvendelse.</p>
                  </div>
                </header>

                <dl className="inquiry-detail-meta-grid">
                  {visibleExtraEntries.map(([key, value]) => (
                    <div key={key} className="inquiry-detail-meta-row">
                      <dt>{key}</dt>
                      <dd>{renderDetailValue(value)}</dd>
                    </div>
                  ))}
                </dl>
              </article>
            ) : null}

          </div>

          <aside className="inquiry-detail-side">
            <LocationMapPreview locations={mapPreviewLocations} />
            <Timeline
              entries={timelineEntries}
              canManageInbox={canManageInbox}
              noteText={noteText}
              onNoteTextChange={(event) => {
                setNoteText(event.target.value);
                if (noteError) {
                  setNoteError(null);
                }
              }}
              noteSaving={noteSaving}
              noteError={noteError}
              activePopover={activePopover}
              onTogglePopover={togglePopover}
              onSubmitNote={handleSubmitNote}
              notePopoverRef={notePopoverRef}
            />
          </aside>

          <article className="card inquiry-detail-secondary-card">
            <header className="card-header inquiry-detail-secondary-header">
              <div>
                <h2>Detaljer</h2>
                <p className="card-subtitle">Sekundær metadata for saken og koblingen i systemet.</p>
              </div>
            </header>

            <dl className="inquiry-detail-meta-grid inquiry-detail-meta-grid-compact">
              {detailFields.map((field) => (
                <div key={field.label} className="inquiry-detail-meta-row inquiry-detail-meta-row-compact">
                  <dt>{field.label}</dt>
                  <dd>{renderDetailValue(field.value)}</dd>
                </div>
              ))}
            </dl>

            {canManageInbox ? (
              <div className="inquiry-detail-danger-zone">
                {deleteError ? <p className="inquiries-note-error">{deleteError}</p> : null}
                <button
                  type="button"
                  className={`button ${deleteConfirmArmed ? 'button-danger-solid' : 'button-danger-outline'}`}
                  onClick={handleDeleteInquiry}
                  disabled={deleteSaving}
                >
                  {deleteSaving ? 'Sletter…' : deleteConfirmArmed ? 'Bekreft sletting' : 'Slett henvendelse'}
                </button>
              </div>
            ) : null}
          </article>
        </div>
      )}
    </div>
  );
};

export default InquiryDetailPage;
